import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Footer } from '../components/Footer';
import { Lock, User, AlertCircle, ArrowLeft, Eye, EyeOff, CheckCircle2, UtensilsCrossed, TrendingUp, Users, BarChart3 } from 'lucide-react';
import { useDocumentTitle } from '../utils/useDocumentTitle';
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Buenos días';
  if (hour < 18) return 'Buenas tardes';
  return 'Buenas noches';
}
export function LoginPage() {
  useDocumentTitle('Iniciar Sesión');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [shakeError, setShakeError] = useState(false);
  const {
    login,
    loginError
  } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    const rememberedUsername = localStorage.getItem('criket_remembered_username');
    if (rememberedUsername) {
      setUsername(rememberedUsername);
      setRememberMe(true);
    }
  }, []);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const success = await login(username, password);
      if (success) {
        if (rememberMe) {
          localStorage.setItem('criket_remembered_username', username);
        } else {
          localStorage.removeItem('criket_remembered_username');
        }
        // Read user from sessionStorage (AuthContext saves to sessionStorage)
        const savedUser = sessionStorage.getItem('criket_user_session');
        const userData = savedUser ? JSON.parse(savedUser) : null;
        if (userData?.isDeliveryOnly) {
          navigate('/admin/delivery');
        } else if (userData?.role === 'employee') {
          navigate('/employee/order');
        } else {
          navigate('/admin/orders');
        }
      } else {
        setError(loginError || 'Usuario o contraseña incorrectos');
        setShakeError(true);
        setTimeout(() => setShakeError(false), 600);
      }
    } catch (err) {
      setError('Error al iniciar sesión. Intenta nuevamente.');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 600);
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-[100dvh] flex font-sans selection:bg-gray-900 selection:text-white" data-id="element-4199">
      {/* ───── Left: Form Side ───── */}
      <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.6,
      ease: 'easeOut'
    }} className="flex-1 flex flex-col bg-white" data-id="element-4200">
        {/* Top nav */}
        <div className="px-6 sm:px-10 lg:px-16 py-6 flex items-center justify-between" data-id="element-4201">
          <Link to="/" className="flex items-center gap-3 group" data-id="element-4202">
            <img src="https://i.imgur.com/bxPZvDb.png" alt="Cricket Logo" className="h-11 w-auto object-contain transition-transform group-hover:scale-105" data-id="element-4203" />
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-400 hover:text-gray-900 transition-colors" data-id="element-4204">
            <ArrowLeft className="w-4 h-4" data-id="element-4205" />
            Volver
          </Link>
        </div>

        {/* Form area */}
        <div className="flex-1 flex items-center justify-center px-6 sm:px-10 lg:px-16 pb-8" data-id="element-4206">
          <div className="w-full max-w-[400px]" data-id="element-4207">
            {/* Mobile: Logo centrado grande */}
            <div className="lg:hidden flex justify-center mb-6" data-id="element-4208"></div>

            {/* Greeting */}
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="mb-10" data-id="element-4209">
              <p className="text-sm font-medium text-gray-400 mb-2" data-id="element-4210">
                {getGreeting()} 👋
              </p>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight leading-tight" data-id="element-4211">
                Bienvenido
                <br data-id="element-4212" />
                de vuelta
              </h1>
            </motion.div>

            {/* Error */}
            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className={`mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3 ${shakeError ? 'animate-[shake_0.5s_ease-in-out]' : ''}`} data-id="element-4213">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-4214">
                  <AlertCircle className="text-red-500" size={16} data-id="element-4215" />
                </div>
                <div data-id="element-4216">
                  <p className="text-sm font-semibold text-red-900" data-id="element-4217">{error}</p>
                  <p className="text-xs text-red-500 mt-0.5" data-id="element-4218">
                    Verifica tus credenciales.
                  </p>
                </div>
              </motion.div>}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-4219">
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3,
              duration: 0.5
            }} data-id="element-4220">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4221">
                  Usuario
                </label>
                <div className="relative group" data-id="element-4222">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-4223">
                    <User className="h-5 w-5 text-gray-300 group-focus-within:text-gray-900 transition-colors" data-id="element-4224" />
                  </div>
                  <input type="text" value={username} onChange={e => setUsername(e.target.value)} className="block w-full pl-12 pr-4 h-13 py-3.5 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-900 bg-white text-gray-900 text-base transition-all placeholder:text-gray-300" placeholder="Tu nombre de usuario" required autoFocus autoComplete="username" data-id="element-4225" />
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.4,
              duration: 0.5
            }} data-id="element-4226">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-4227">
                  Contraseña
                </label>
                <div className="relative group" data-id="element-4228">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-4229">
                    <Lock className="h-5 w-5 text-gray-300 group-focus-within:text-gray-900 transition-colors" data-id="element-4230" />
                  </div>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-12 pr-14 h-13 py-3.5 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-0 focus:border-gray-900 bg-white text-gray-900 text-base transition-all placeholder:text-gray-300" placeholder="Tu contraseña" required autoComplete="current-password" data-id="element-4231" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-300 hover:text-gray-600 transition-colors" tabIndex={-1} data-id="element-4232">
                    {showPassword ? <EyeOff className="h-5 w-5" data-id="element-4233" /> : <Eye className="h-5 w-5" data-id="element-4234" />}
                  </button>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.5
            }} className="flex items-center" data-id="element-4235">
                <label className="flex items-center gap-2.5 cursor-pointer select-none group" data-id="element-4236">
                  <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900 cursor-pointer" data-id="element-4237" />
                  <span className="text-sm text-gray-500 group-hover:text-gray-700 transition-colors" data-id="element-4238">
                    Recordar mi usuario
                  </span>
                </label>
              </motion.div>

              <motion.button initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6
            }} whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} type="submit" disabled={isLoading} className="w-full h-13 py-3.5 bg-gray-900 text-white rounded-2xl text-base font-semibold shadow-lg shadow-gray-900/10 hover:bg-gray-800 hover:shadow-xl hover:shadow-gray-900/20 hover:-translate-y-0.5 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2" data-id="element-4239">
                {isLoading ? <>
                    <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" data-id="element-4240">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" data-id="element-4241" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-4242" />
                    </svg>
                    Verificando...
                  </> : 'Entrar'}
              </motion.button>
            </form>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.8
          }} className="mt-8 text-sm text-gray-400 text-center" data-id="element-4243">
              ¿No puedes acceder?{' '}
              <a href="mailto:soporte@cricket.do" className="text-gray-900 font-semibold hover:underline underline-offset-2" data-id="element-4244">
                Soporte
              </a>
            </motion.p>
          </div>
        </div>

        <Footer data-id="element-4245" />
      </motion.div>

      {/* ───── Right: Brand Panel (desktop only) ───── */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 1,
      delay: 0.2
    }} className="hidden lg:flex w-[48%] bg-gray-950 relative overflow-hidden flex-col justify-between p-12 xl:p-16" data-id="element-4246">
        {/* Decorative layers */}
        <div className="absolute inset-0 opacity-[0.04] bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:48px_48px]" data-id="element-4247"></div>
        <div className="absolute top-[-15%] right-[-10%] w-[500px] h-[500px] bg-green-500/[0.04] rounded-full blur-[120px] pointer-events-none" data-id="element-4248"></div>
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-white/[0.02] rounded-full blur-[100px] pointer-events-none" data-id="element-4249"></div>

        {/* Center - Mockup visual + texto */}
        <div className="relative z-10 flex-1 flex flex-col justify-center py-8" data-id="element-4250">
          {/* App Mockup */}
          <motion.div initial={{
          y: 30,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="relative mb-12" data-id="element-4251">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-full border border-gray-800/50 opacity-50" data-id="element-4252"></div>

            <div className="relative z-10 bg-white rounded-3xl shadow-2xl border border-gray-100 p-5 max-w-sm mx-auto transform -rotate-2 hover:rotate-0 transition-transform duration-500" data-id="element-4253">
              {/* Header Mockup */}
              <div className="flex items-center justify-between mb-5" data-id="element-4254">
                <div data-id="element-4255">
                  <h3 className="font-bold text-lg text-gray-900" data-id="element-4256">
                    Hola, Carlos 👋
                  </h3>
                  <p className="text-xs text-gray-400" data-id="element-4257">
                    Subsidio:{' '}
                    <span className="text-green-600 font-semibold" data-id="element-4258">
                      RD$ 350.00
                    </span>
                  </p>
                </div>
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center" data-id="element-4259">
                  <UtensilsCrossed className="w-5 h-5 text-gray-800" data-id="element-4260" />
                </div>
              </div>

              {/* Item 1 */}
              <div className="bg-gray-50 rounded-xl p-3 mb-3 flex gap-3 border border-gray-100" data-id="element-4261">
                <div className="w-12 h-12 bg-white rounded-lg flex-shrink-0 flex items-center justify-center text-2xl shadow-sm" data-id="element-4262">
                  🍗
                </div>
                <div className="flex-1" data-id="element-4263">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-4264">
                    Pollo al Horno
                  </p>
                  <p className="text-[10px] text-gray-400" data-id="element-4265">
                    Con arroz y ensalada
                  </p>
                  <div className="flex justify-between items-center mt-1" data-id="element-4266">
                    <span className="text-xs font-bold text-gray-900" data-id="element-4267">
                      RD$ 250.00
                    </span>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[9px] font-bold rounded-full" data-id="element-4268">
                      Subsidiado
                    </span>
                  </div>
                </div>
              </div>

              {/* Item 2 */}
              <div className="bg-white rounded-xl p-3 mb-4 flex gap-3 border border-gray-100 shadow-sm" data-id="element-4269">
                <div className="w-12 h-12 bg-gray-50 rounded-lg flex-shrink-0 flex items-center justify-center text-2xl" data-id="element-4270">
                  🥗
                </div>
                <div className="flex-1" data-id="element-4271">
                  <p className="text-sm font-semibold text-gray-900" data-id="element-4272">
                    Ensalada César
                  </p>
                  <p className="text-[10px] text-gray-400" data-id="element-4273">
                    Con pollo grillado
                  </p>
                  <div className="flex justify-between items-center mt-1" data-id="element-4274">
                    <span className="text-xs font-bold text-gray-900" data-id="element-4275">
                      RD$ 280.00
                    </span>
                    <button className="px-3 py-1 bg-gray-900 rounded-lg text-white text-[10px] font-bold" data-id="element-4276">
                      Agregar
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer Mockup */}
              <div className="pt-4 border-t border-gray-100" data-id="element-4277">
                <div className="flex justify-between mb-3" data-id="element-4278">
                  <span className="text-xs text-gray-500" data-id="element-4279">Total a pagar</span>
                  <span className="text-sm font-bold text-gray-900" data-id="element-4280">
                    RD$ 0.00
                  </span>
                </div>
                <button className="w-full py-2.5 bg-gray-900 text-white rounded-xl text-xs font-bold shadow-lg shadow-gray-900/20" data-id="element-4281">
                  Confirmar Pedido
                </button>
              </div>
            </div>

            {/* Floating Card 1 */}
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 1,
            type: 'spring'
          }} className="absolute -bottom-4 -right-8 bg-white p-3 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-3 z-20" style={{
            animation: 'float 3s ease-in-out infinite'
          }} data-id="element-4282">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-4283">
                <CheckCircle2 className="w-4 h-4" data-id="element-4284" />
              </div>
              <div data-id="element-4285">
                <p className="text-[10px] text-gray-400 font-medium" data-id="element-4286">
                  Ahora mismo
                </p>
                <p className="text-xs font-bold text-gray-900" data-id="element-4287">
                  Pedido Entregado ✓
                </p>
              </div>
            </motion.div>

            {/* Floating Card 2 */}
            <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: 1.2,
            type: 'spring'
          }} className="absolute -top-2 -left-8 bg-white p-3 rounded-2xl shadow-xl border border-gray-100 flex items-center gap-3 z-20" style={{
            animation: 'float 3s ease-in-out infinite 1.5s'
          }} data-id="element-4288">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600" data-id="element-4289">
                <TrendingUp className="w-4 h-4" data-id="element-4290" />
              </div>
              <div data-id="element-4291">
                <p className="text-[10px] text-gray-400 font-medium" data-id="element-4292">Hoy</p>
                <p className="text-xs font-bold text-gray-900" data-id="element-4293">127 pedidos</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Text content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.6,
          duration: 0.6
        }} data-id="element-4294">
            <h2 className="text-2xl xl:text-3xl font-bold text-white leading-tight mb-4" data-id="element-4295">
              Todo el almuerzo de tu empresa, en un solo lugar.
            </h2>

            <div className="space-y-3" data-id="element-4296">
              {[{
              icon: <UtensilsCrossed className="w-4 h-4" data-id="element-4297" />,
              text: 'Menú digital actualizado cada día'
            }, {
              icon: <BarChart3 className="w-4 h-4" data-id="element-4298" />,
              text: 'Reportes de consumo en tiempo real'
            }, {
              icon: <Users className="w-4 h-4" data-id="element-4299" />,
              text: 'Gestión multi-sede y departamentos'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.8 + i * 0.1
            }} className="flex items-center gap-3" data-id="element-4300">
                  <div className="w-7 h-7 rounded-lg bg-white/[0.06] flex items-center justify-center text-green-400 flex-shrink-0" data-id="element-4301">
                    {item.icon}
                  </div>
                  <span className="text-sm text-gray-400" data-id="element-4302">{item.text}</span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>

        {/* Bottom - Social proof */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.2
      }} className="relative z-10 pt-6 border-t border-white/[0.06]" data-id="element-4303">
          <div className="flex items-center gap-4" data-id="element-4304">
            <div className="flex -space-x-2" data-id="element-4305">
              {['MR', 'CM', 'AP', 'JL'].map((initials, i) => <div key={i} className="w-8 h-8 rounded-full border-2 border-gray-950 bg-white/10 flex items-center justify-center text-[10px] font-bold text-white" data-id="element-4306">
                  {initials}
                </div>)}
            </div>
            <p className="text-sm text-gray-500" data-id="element-4307">
              <span className="text-white font-semibold" data-id="element-4308">50+ empresas</span>{' '}
              confían en Cricket
            </p>
          </div>
        </motion.div>
      </motion.div>

      <style data-id="element-4309">{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        /* Hide native browser password reveal button */
        input::-ms-reveal,
        input::-ms-clear {
          display: none;
        }
        input::-webkit-credentials-auto-fill-button,
        input::-webkit-textfield-decoration-container {
          visibility: hidden;
          pointer-events: none;
        }
      `}</style>
    </div>;
}